<template>
  <DialogPreInstallMethodC
    v-if="isMethodC"
    v-model="isOpen"
    :task="task"
    @task-updated="(payload) => $emit('task-updated', payload)"
    @close="$emit('close')"
  />
  <DialogLayout v-else v-model="isOpen" :title="modalTitle" width="1200px">
    <AlertsAnimated :alerts="localAlerts" />

    <div class="pa-4 border-bottom">
      <v-row no-gutters class="mb-3">
        <v-col cols="7">
          <div class="task-details">
            <div class="mb-2 d-flex align-center">
              <span class="font-weight-medium mr-2">Task ID: </span>
              <span class="mr-3">{{ task.taskId }}</span>
            </div>
            <div class="mb-2">
              <span class="font-weight-medium">Due Date: </span>
              <span>{{ formatDateSlashUTC(task.dueAt) }}</span>
            </div>
            <div v-if="task && task.description">
              <span class="font-weight-medium">Description: </span>
              <span>{{ task.description }}</span>
            </div>
          </div>
        </v-col>

        <v-col v-show="!isTaskDone" cols="5">
          <div class="qr-section">
            <div class="d-flex justify-space-between align-center mb-2">
              <span class="font-weight-medium">Mobile Access:</span>
              <v-btn
                x-small
                color="primary"
                outlined
                @click="showQRDialog = true"
              >
                <v-icon x-small left>mdi-qrcode</v-icon>
                Generate QR
              </v-btn>
            </div>
          </div>
        </v-col>
      </v-row>
    </div>

    <div v-if="loadingDevicesOrUnits" class="pa-4">
      <v-skeleton-loader
        v-for="n in 5"
        :key="n"
        :loading="loadingDevicesOrUnits"
        type="card"
        height="70"
        class="mb-2"
      />
    </div>

    <div v-else-if="!isMethodC" class="pa-4">
      <div class="assignment-header mb-4">
        <div class="d-flex justify-space-between align-center">
          <span class="font-weight-bold">Device to Unit Assignment</span>
          <span class="text-caption grey--text">
            {{ completedAssignments }}/{{ targetCount }} assigned
          </span>
        </div>
        <v-progress-linear
          :value="progressPercentage"
          color="success"
          height="4"
          class="mt-2"
        />
      </div>

      <!-- Assignment Table -->
      <v-card outlined>
        <v-card-title class="py-2">
          <span class="subtitle-2">Pre-Install Assignments</span>
          <v-spacer />
          <span class="caption grey--text">
            Min: {{ targetCount }} · Max: {{ maxAssignments }}
          </span>
        </v-card-title>
        <v-divider />

        <!-- Table Headers -->
        <v-row no-gutters class="pa-3 grey lighten-4">
          <v-col cols="3" class="font-weight-medium">Unit</v-col>
          <v-col cols="3" class="font-weight-medium">Device</v-col>
          <v-col cols="3" class="font-weight-medium text-center"
            >Comments</v-col
          >
          <v-col cols="3" class="font-weight-medium text-center">{{
            isTaskDone ? "Status" : "Action"
          }}</v-col>
        </v-row>
        <v-divider />

        <!-- Assignment Rows -->
        <div class="assignment-list">
          <v-row
            v-for="(assignment, index) in displayAssignments"
            :key="`assignment-${index}`"
            no-gutters
            class="assignment-row pa-3"
            :class="{
              'assignment-completed':
                assignment.locallyAssigned || assignment.completed,
            }"
          >
            <!-- Unit Column (first — always active per PRD) -->
            <v-col cols="3" class="px-2">
              <!-- Locked: show unit name as static text -->
              <div
                v-if="
                  isTaskDone ||
                  assignment.locallyAssigned ||
                  assignment.completed
                "
                class="assigned-cell"
              >
                {{
                  (assignment.selectedUnit && assignment.selectedUnit.name) ||
                  (assignment.selectedUnitId
                    ? "Unit " + assignment.selectedUnitId
                    : "—")
                }}
              </div>
              <!-- Editable unit selection (always enabled) -->
              <v-autocomplete
                v-else
                :key="`unit-${assignment.id}`"
                v-model="assignment.pendingUnitId"
                :items="unitsForAssignment()"
                :item-text="(u) => u.name"
                item-value="id"
                placeholder="Select Unit"
                dense
                outlined
                hide-details
                clearable
                @change="onUnitChanged(assignment, $event)"
              >
                <template #item="{ item }">
                  <v-list-item-content>
                    <v-list-item-title>{{ item.name }}</v-list-item-title>
                    <v-list-item-subtitle
                      v-if="unitTypeName(item)"
                      class="text-caption"
                    >
                      {{ unitTypeName(item) }}
                    </v-list-item-subtitle>
                  </v-list-item-content>
                </template>
                <template #no-data>
                  <v-list-item>
                    <v-list-item-content>
                      <v-list-item-subtitle class="font-italic">
                        No vacant units available
                      </v-list-item-subtitle>
                    </v-list-item-content>
                  </v-list-item>
                </template>
              </v-autocomplete>
            </v-col>

            <!-- Device Column (second — enabled only after unit selected) -->
            <v-col cols="3" class="pr-2">
              <!-- Locked: show device name as static text -->
              <div
                v-if="
                  isTaskDone ||
                  assignment.locallyAssigned ||
                  assignment.completed
                "
                class="assigned-cell"
              >
                {{ formatDeviceDisplayName(assignment.selectedDevice) }}
              </div>
              <!-- Editable device selection (only when unit is selected) -->
              <v-autocomplete
                v-else-if="assignment.pendingUnitId"
                v-model="assignment.selectedDeviceId"
                :items="availableDevicesForAssignment(assignment)"
                :item-text="formatDeviceDisplayName"
                item-value="id"
                placeholder="Select Device"
                dense
                outlined
                clearable
                hide-details
                @change="updateDeviceSelection(assignment, index)"
              >
                <template #selection="{ item }">
                  <span class="text-truncate">{{
                    formatDeviceDisplayName(item)
                  }}</span>
                </template>
                <template #item="{ item }">
                  <v-list-item-content>
                    <v-list-item-title>{{
                      formatDeviceDisplayName(item)
                    }}</v-list-item-title>
                    <v-list-item-subtitle class="text-caption">
                      ID: {{ item.shortId || item.externalId }} · Status:
                      {{ titleCase(item.status) }}
                    </v-list-item-subtitle>
                  </v-list-item-content>
                </template>
                <template #no-data>
                  <v-list-item>
                    <v-list-item-content>
                      <v-list-item-subtitle class="font-italic">
                        No available devices
                      </v-list-item-subtitle>
                    </v-list-item-content>
                  </v-list-item>
                </template>
              </v-autocomplete>
              <!-- No unit selected yet — device is locked -->
              <v-autocomplete
                v-else
                :items="[]"
                placeholder="Select a unit first"
                dense
                outlined
                hide-details
                disabled
              />
            </v-col>

            <v-col cols="3">
              <div v-if="isTaskDone" class="assigned-cell justify-center">
                {{ assignment.comment || "N/A" }}
              </div>
              <v-text-field
                v-else
                v-model="assignment.comment"
                placeholder="Comments (optional)..."
                dense
                outlined
                hide-details
                class="comment-field"
              />
            </v-col>

            <!-- Action / Status Column -->
            <v-col cols="3" class="pl-2 d-flex align-center justify-center">
              <!-- Done task: show Assigned badge -->
              <span v-if="isTaskDone" class="d-flex align-center">
                <v-icon color="success" small left>mdi-check-circle</v-icon>
                Assigned
              </span>

              <template v-else-if="assignment.locallyAssigned">
                <span class="mx-5 d-flex align-center">
                  <v-icon color="success" small left>mdi-check-circle</v-icon>
                  Assigned
                </span>
                <v-btn
                  icon
                  x-small
                  color="error"
                  class="ml-3"
                  title="Remove this assignment"
                  @click="unassignDevice(assignment)"
                >
                  <v-icon>mdi-close-circle-outline</v-icon>
                </v-btn>
              </template>

              <!-- Already saved to DB (completed): locked, no remove -->
              <span
                v-else-if="assignment.completed"
                class="success--text d-flex align-center"
              >
                <v-icon color="success" small left>mdi-check-circle</v-icon>
                Assigned
              </span>

              <!-- Unit selected: Assign button (disabled until device also selected) -->
              <template v-else-if="assignment.pendingUnitId">
                <v-btn
                  small
                  color="success"
                  :disabled="!assignment.selectedDevice"
                  class="mx-5"
                  @click="locallyAssignDevice(assignment)"
                >
                  <v-icon small left>mdi-link</v-icon>
                  Assign
                </v-btn>
                <v-btn
                  icon
                  x-small
                  color="error"
                  title="Remove this assignment"
                  :disabled="deviceAssignments.length <= 1"
                  @click="removeAssignment(index)"
                >
                  <v-icon>mdi-close-circle-outline</v-icon>
                </v-btn>
              </template>

              <!-- No unit selected yet -->
              <template v-else>
                <v-btn small color="grey" class="mx-3" disabled>
                  Select Unit
                </v-btn>
                <v-btn
                  icon
                  x-small
                  color="error"
                  class="ml-1"
                  :disabled="deviceAssignments.length <= 1"
                  @click="removeAssignment(index)"
                >
                  <v-icon>mdi-close-circle-outline</v-icon>
                </v-btn>
              </template>
            </v-col>
          </v-row>
        </div>

        <v-divider />

        <div v-if="!isTaskDone" class="pa-3">
          <v-btn
            outlined
            color="primary"
            @click="addAssignment"
            :disabled="!canAddMoreAssignments"
          >
            <v-icon left>mdi-plus</v-icon>
            Add New Assignment
          </v-btn>
          <span
            v-if="
              !canAddMoreAssignments &&
              deviceAssignments.length >= maxAssignments
            "
            class="ml-2 caption grey--text"
          >
            Maximum {{ maxAssignments }} assignments reached
          </span>
        </div>
      </v-card>
    </div>

    <template #actions>
      <v-btn
        v-if="shouldShowActionButton"
        color="success"
        :disabled="!canCompleteTask"
        @click="completePreInstallTask"
        depressed
        large
        :loading="completing"
        class="ml-2"
      >
        {{
          task && task.resolution === "done"
            ? "Save Additional Assignments"
            : "Complete Pre-Install Task"
        }}
      </v-btn>
    </template>

    <QRCodeDialog
      v-model="showQRDialog"
      title="Mobile Task Access"
      description="Scan this QR code with your mobile phone or tablet to manage this pre-install task."
      :url-generator="generatePreInstallUrl"
    />
  </DialogLayout>
</template>

<script>
import DialogLayout from "@layout/DialogLayout.vue";
import DialogPreInstallMethodC from "./DialogPreInstallMethodC.vue";
import QRCodeDialog from "../mobile/QRCodeDialog.vue";
import { UPDATE_TASK } from "./graphql";
import { PREINSTALLED_INVENTORY_UNITS } from "@components/smart_units/graphql";
import { formatDateSlashUTC } from "@tod-ui/helpers/datetime";
import useUser from "@components/authentication/useUser";
import AlertsAnimated from "@tod-ui/components/AlertsAnimated.vue";
import useAlerts from "@tod-ui/composables/useAlerts";
import useInventory from "@components/smart_units/useInventory";
import useDevice from "@components/smart_units/useDevice";
import { titleCase } from "@tod-ui/helpers/strings";

export default {
  name: "DialogPreInstallModal",
  components: {
    DialogLayout,
    DialogPreInstallMethodC,
    AlertsAnimated,
    QRCodeDialog,
  },
  props: {
    value: {
      type: Boolean,
      default: false,
    },
    task: {
      type: Object,
      required: false,
      default: null,
    },
  },
  setup() {
    const { defaultOrganization } = useUser();
    const { addLocalAlert, clearAllAlerts, localAlerts } = useAlerts();
    const {
      officeInventory,
      officeInventoryLoading,
      fetchTabData,
      formatDeviceName,
    } = useInventory();

    return {
      defaultOrganization,
      addLocalAlert,
      clearAllAlerts,
      localAlerts,
      officeInventory,
      officeInventoryLoading,
      fetchTabData,
      formatDeviceName,
    };
  },
  data() {
    return {
      deviceAssignments: [],
      completing: false,
      showQRDialog: false,
      vacantUnits: [],
      vacantUnitsLoading: false,
    };
  },
  computed: {
    isOpen: {
      get() {
        return this.value;
      },
      set(value) {
        this.$emit("input", value);
        if (!value) {
          this.$emit("close");
          this.clearAllAlerts();
          this.deviceAssignments = [];
        }
      },
    },
    modalTitle() {
      return (this.task && this.task.title) || "Pre-Install Devices";
    },
    availableDevices() {
      const devices = this.officeInventory || [];
      return devices
        .filter(
          (device) =>
            device.type === "device" && device.devices && device.devices[0]
        )
        .map((device) => {
          const deviceData = device.devices[0];
          return {
            id: deviceData.id,
            name:
              this.formatDeviceName(deviceData) ||
              deviceData.shortId ||
              deviceData.externalId ||
              `Device ${deviceData.id}`,
            shortId: deviceData.shortId || "",
            externalId: deviceData.externalId || "",
            status: deviceData.status || "unknown",
            ...deviceData,
          };
        });
    },
    availableUnits() {
      return this.vacantUnits;
    },

    // Authoritative target count from server (set by scheduler at task creation).
    // Falls back to rule conditions if not yet populated.
    targetCount() {
      const serverTarget =
        this.task && this.task.actions && this.task.actions.targetCount;
      if (serverTarget && serverTarget > 0) return serverTarget;

      const c =
        this.task && this.task.taskRule && this.task.taskRule.conditions;
      if (!c || !c.method) {
        const checklistLen =
          this.task && this.task.actions && this.task.actions.checklist
            ? this.task.actions.checklist.length
            : 0;
        return Math.max(checklistLen, 1);
      }
      if (c.method === "vacancy_threshold") {
        return c.targetAtOrAboveThreshold || c.targetBelowThreshold || 1;
      }
      if (c.method === "high_penetration") return c.fixedTargetUnits || 1;
      if (c.method === "unit_type_coverage") return c.devicesPerUnit || 1;
      return 1;
    },

    // Maximum assignments = 3× the target
    maxAssignments() {
      return this.targetCount * 3;
    },

    preInstallMethod() {
      return (
        (this.task &&
          this.task.actions &&
          this.task.actions.preInstallMethod) ||
        (this.task &&
          this.task.taskRule &&
          this.task.taskRule.conditions &&
          this.task.taskRule.conditions.method) ||
        null
      );
    },

    // Ranked unit type names from server (e.g. ["10x10", "10x13", "5x10"])
    unitTypeRanking() {
      return (
        (this.task && this.task.actions && this.task.actions.unitTypeRanking) ||
        []
      );
    },

    // Map of unit_type_name => vacant count (e.g. {"10x10": 15, "10x13": 8})
    vacantByType() {
      return (
        (this.task && this.task.actions && this.task.actions.vacantByType) || {}
      );
    },

    completedAssignments() {
      return this.deviceAssignments.filter(
        (a) => a.locallyAssigned || a.completed
      ).length;
    },
    totalAssignments() {
      return this.deviceAssignments.length;
    },
    progressPercentage() {
      return this.targetCount > 0
        ? (this.completedAssignments / this.targetCount) * 100
        : 0;
    },
    isTaskDone() {
      return this.task && this.task.resolution === "done";
    },
    shouldShowActionButton() {
      return this.task && !this.loadingDevicesOrUnits && !this.isTaskDone;
    },
    canCompleteTask() {
      return this.deviceAssignments.some((a) => a.locallyAssigned);
    },
    canAddMoreAssignments() {
      return (
        this.availableDevices.length > 0 &&
        this.availableUnits.length > 0 &&
        this.deviceAssignments.length < this.maxAssignments
      );
    },
    loadingDevicesOrUnits() {
      return this.officeInventoryLoading || this.vacantUnitsLoading;
    },
    displayAssignments() {
      if (this.isTaskDone) {
        return this.deviceAssignments.filter(
          (a) => a.completed || a.locallyAssigned
        );
      }
      return this.deviceAssignments;
    },
    // Reactive Set of device IDs owned by locked (assigned/completed) rows.
    // Using a computed ensures Vue 2 tracks nested property dependencies
    // (locallyAssigned, selectedDeviceId, selectedDevice.id) correctly so that
    // availableDevicesForAssignment re-evaluates whenever a row is locked.
    lockedDeviceIds() {
      const ids = new Set();
      this.deviceAssignments.forEach((a) => {
        if (!(a.locallyAssigned || a.completed)) return;
        if (a.selectedDeviceId) ids.add(a.selectedDeviceId);
        if (a.selectedDevice && a.selectedDevice.id)
          ids.add(a.selectedDevice.id);
      });
      return ids;
    },

    // True when this task rule uses Method C — the entire body is delegated
    // to DialogPreInstallMethodC. Kept here so the outer v-if picks which
    // dialog to mount.
    isMethodC() {
      return this.preInstallMethod === "unit_type_coverage";
    },
  },
  watch: {
    value: {
      handler(newVal) {
        if (newVal && this.task) {
          // Always reset on every open
          this.deviceAssignments = [];
          this.clearAllAlerts();
          if (!this.isMethodC) this.initializeModal();
        }
      },
      immediate: true,
    },
    task: {
      handler(newTask, oldTask) {
        if (!newTask || !this.value) return;
        if (this.isMethodC) return;
        const newId = newTask.id;
        const oldId = oldTask && oldTask.id;
        if (newId !== oldId) {
          // Different task — always reinitialize to prevent data bleed between tasks
          this.deviceAssignments = [];
          this.initializeModal();
        } else if (this.deviceAssignments.length === 0) {
          // Same task, not yet initialized (e.g. task prop arrived after value=true)
          this.initializeModal();
        }
      },
      deep: true,
      immediate: true,
    },
  },
  methods: {
    // -----------------------------------------------------------------------
    // Initialization
    // -----------------------------------------------------------------------
    async initializeModal() {
      await this.loadInventoryData();
      this.populateExistingAssignments();
      this.autoFillRowsByMethod();
    },

    async loadInventoryData() {
      const facilityId =
        this.task && this.task.organization && this.task.organization.id;
      if (!facilityId) return;

      try {
        // Tab 0: office inventory devices (in_service, not yet assigned to a unit)
        await this.fetchTabData(
          0,
          this.$apollo.provider.defaultClient,
          100,
          "",
          facilityId
        );
        // Vacant units: query directly with deviceAssigned: false so we only show
        // units that don't already have a device installed
        await this.loadVacantUnits(facilityId);
        await this.$nextTick();
      } catch (error) {
        console.error("Error loading inventory data:", error);
        this.addLocalAlert({
          type: "error",
          message: "Error loading inventory data. Please try again.",
          timeout: 5,
        });
      }
    },

    async loadVacantUnits(facilityId) {
      this.vacantUnitsLoading = true;
      try {
        // Method A (vacancy_threshold): sort by longest vacant first per PRD.
        // "vacancy_duration asc" is a special sort handled by the backend that
        // orders by MAX(billing_account.updated_at) ASC NULLS FIRST — units
        // never occupied (NULL) come first, then oldest last-occupied first.
        const sort =
          this.preInstallMethod === "vacancy_threshold"
            ? "vacancy_duration asc"
            : "name asc";

        const result = await this.$apollo.query({
          query: PREINSTALLED_INVENTORY_UNITS,
          variables: {
            id: facilityId,
            assigned: false,
            deviceAssigned: false,
            type: "zone",
            subtype: "unit",
            limit: 500,
            page: 1,
            sort,
          },
          fetchPolicy: "network-only",
        });
        const data =
          result.data && result.data.organizationDescendantsPaginated;
        this.vacantUnits = (data && data.data) || [];
      } finally {
        this.vacantUnitsLoading = false;
      }
    },

    // Seed rows from server checklist (completed items only — already saved)
    populateExistingAssignments() {
      const actions = this.task && this.task.actions;

      // Prefer structured assignments array (set on task completion)
      const savedAssignments =
        actions && Array.isArray(actions.assignments)
          ? actions.assignments
          : null;
      if (savedAssignments && savedAssignments.length > 0) {
        this.deviceAssignments = savedAssignments.map((item, idx) => {
          // Backend stores snake_case keys in JSONB; `:json` scalar returns them as-is.
          // Support both snake_case (from DB) and camelCase (in-session objects).
          const deviceName = item.device_name || item.deviceName || null;
          const unitName = item.unit_name || item.unitName || null;
          return {
            id: `restored-${idx}`,
            selectedDeviceId: null,
            selectedUnitId: null,
            selectedDevice: deviceName ? { id: null, name: deviceName } : null,
            selectedUnit: unitName ? { id: null, name: unitName } : null,
            pendingUnitId: null,
            locallyAssigned: false,
            completed: true,
            comment: item.comment || "",
            unitTypeFilter: null,
          };
        });
        return;
      }

      // Fallback: parse description field ("deviceName → unitName") from checklist
      const checklist =
        actions && Array.isArray(actions.checklist) ? actions.checklist : [];

      this.deviceAssignments = checklist.map((item) => {
        let selectedDevice = null;
        let selectedUnit = null;
        if (
          item.completed &&
          item.description &&
          item.description.includes(" → ")
        ) {
          const [deviceName, unitName] = item.description.split(" → ");
          selectedDevice = { id: null, name: deviceName.trim() };
          selectedUnit = { id: null, name: unitName.trim() };
        }
        return {
          id: item.id,
          selectedDeviceId: null,
          selectedUnitId: null,
          selectedDevice,
          selectedUnit,
          pendingUnitId: null,
          locallyAssigned: false,
          completed: Boolean(item.completed),
          comment: item.comment || "",
          unitTypeFilter: null,
        };
      });
    },

    // Top up rows to targetCount without disturbing already-restored rows
    ensureMinimumRows() {
      const method = this.preInstallMethod;
      const topType =
        method === "high_penetration" ? this.unitTypeRanking[0] || null : null;
      while (this.deviceAssignments.length < this.targetCount) {
        this.deviceAssignments.push(this.newAssignmentRow(topType));
      }
    },

    // Auto-fill empty rows based on method
    autoFillRowsByMethod() {
      const method = this.preInstallMethod;

      if (method === "high_penetration") {
        // Method B: targetCount rows, all pre-filtered to top-ranked type
        this.fillRowsForHighPenetration();
      } else {
        // Method A (vacancy_threshold) and Method C (unit_type_coverage):
        // PRD — both show plain rows up to targetCount, no type filtering.
        while (this.deviceAssignments.length < this.targetCount) {
          this.addAssignment();
        }
      }
    },

    // Method B: all rows pre-filtered to the highest-demand unit type
    fillRowsForHighPenetration() {
      const topType = this.unitTypeRanking[0] || null;
      while (this.deviceAssignments.length < this.targetCount) {
        this.deviceAssignments.push(this.newAssignmentRow(topType));
      }
    },

    // Method C: one row per ranked unit type, then fill remaining with unranked
    fillRowsForUnitTypeCoverage() {
      // Remove blank rows, keep completed ones
      const existing = this.deviceAssignments.filter((a) => a.completed);
      const completedTypes = new Set(
        existing.map((a) => a.unitTypeFilter).filter(Boolean)
      );

      const typesToAdd = this.unitTypeRanking.filter(
        (t) => !completedTypes.has(t)
      );

      this.deviceAssignments = [
        ...existing,
        ...typesToAdd.map((type) => this.newAssignmentRow(type)),
      ];

      // If no ranking data, fall back to targetCount blank rows
      if (this.deviceAssignments.length === 0) {
        while (this.deviceAssignments.length < this.targetCount) {
          this.addAssignment();
        }
      }
    },

    newAssignmentRow(unitTypeFilter = null) {
      return {
        id: `temp-${Date.now()}-${Math.random()}`,
        selectedDeviceId: null,
        selectedUnitId: null,
        selectedDevice: null,
        selectedUnit: null,
        pendingUnitId: null,
        locallyAssigned: false,
        completed: false,
        comment: "",
        unitTypeFilter,
      };
    },

    // -----------------------------------------------------------------------
    // Unit dropdown filtering & sorting per method
    // -----------------------------------------------------------------------

    // Returns units for a specific assignment row, ordered by method logic.
    // Method A: longest vacant first (backend sort)
    // Method B: sorted by move-in ranking (highest demand first)
    // Method C: all vacant units excluding exterior parking
    unitsForAssignment() {
      const units = this.availableUnits;
      const method = this.preInstallMethod;

      if (method === "high_penetration") {
        // Method B: sort by move-in ranking (highest demand first per PRD)
        if (this.unitTypeRanking.length === 0) return units;
        const rankIndex = (unit) => {
          const typeName = this.unitTypeName(unit);
          const idx = this.unitTypeRanking.indexOf(typeName);
          return idx === -1 ? this.unitTypeRanking.length : idx;
        };
        return [...units].sort((a, b) => rankIndex(a) - rankIndex(b));
      }

      if (method === "unit_type_coverage") {
        // Method C: exclude exterior parking units per PRD
        return units.filter((unit) => {
          const typeName = (this.unitTypeName(unit) || "").toLowerCase();
          return !typeName.includes("parking");
        });
      }

      // Method A (vacancy_threshold): plain list — backend already sorted by vacancy duration asc
      return units;
    },

    // Derive unit type name from a unit object.
    // Tries unitType.name (from inventory), then falls back to parsing unit.name.
    unitTypeName(unit) {
      if (!unit) return null;
      if (unit.unitType && unit.unitType.name) return unit.unitType.name;
      // Common pattern: unit name is "101 (10x10)" or "10x10 - A1"
      const match = unit.name && unit.name.match(/(\d+x\d+)/i);
      return match ? match[1] : null;
    },

    // -----------------------------------------------------------------------
    // Device helpers
    // -----------------------------------------------------------------------
    findDeviceById(deviceId) {
      return this.availableDevices.find((device) => device.id === deviceId);
    },

    findUnitById(unitId) {
      return this.availableUnits.find((unit) => unit.id === unitId);
    },

    formatDeviceDisplayName(device) {
      if (!device) return "";
      return (
        this.formatDeviceName(device) ||
        device.name ||
        device.shortId ||
        device.externalId ||
        `Device ${device.id}`
      );
    },

    availableDevicesForAssignment(currentAssignment) {
      const liveDevices = this.availableDevices;

      const extraDevices =
        currentAssignment.selectedDevice &&
        currentAssignment.selectedDeviceId &&
        !liveDevices.some((d) => d.id === currentAssignment.selectedDeviceId)
          ? [currentAssignment.selectedDevice]
          : [];

      const devicePool = [...extraDevices, ...liveDevices];

      return devicePool.filter((device) => {
        const deviceId = device.id;

        // Rule 1: device is owned by a locked row → exclude unconditionally.
        // Uses the computed lockedDeviceIds so Vue 2 tracks this reactively.
        if (this.lockedDeviceIds.has(deviceId)) return false;

        // Rule 2: device is this row's own current selection → keep it
        if (
          currentAssignment.selectedDeviceId === deviceId ||
          (currentAssignment.selectedDevice &&
            currentAssignment.selectedDevice.id === deviceId)
        )
          return true;

        // Rule 3: device is selected (pending) by another row → exclude
        return !this.deviceAssignments.some(
          (a) =>
            a !== currentAssignment &&
            !(a.locallyAssigned || a.completed) &&
            (a.selectedDeviceId === deviceId ||
              (a.selectedDevice && a.selectedDevice.id === deviceId))
        );
      });
    },

    updateDeviceSelection(assignment) {
      if (assignment.selectedDeviceId) {
        assignment.selectedDevice =
          this.findDeviceById(assignment.selectedDeviceId) ||
          assignment.selectedDevice;
      } else {
        // Device cleared — keep unit selection (unit-first flow)
        assignment.selectedDevice = null;
      }
      assignment.locallyAssigned = false;
    },

    onUnitChanged(assignment, unitId) {
      assignment.pendingUnitId = unitId || null;
      if (!unitId) {
        // Unit cleared — reset device too since device depends on unit
        assignment.selectedDeviceId = null;
        assignment.selectedDevice = null;
      }
    },

    // -----------------------------------------------------------------------
    // Local assignment (mark as assigned in-memory)
    // -----------------------------------------------------------------------
    locallyAssignDevice(assignment) {
      if (!assignment.selectedDevice || !assignment.pendingUnitId) {
        this.addLocalAlert({
          type: "warning",
          message: "Please select both a device and a unit before assigning.",
          timeout: 3,
        });
        return;
      }
      assignment.locallyAssigned = true;
      assignment.selectedUnitId = assignment.pendingUnitId;
      assignment.selectedUnit = this.findUnitById(assignment.pendingUnitId);
    },

    unassignDevice(assignment) {
      assignment.locallyAssigned = false;
      assignment.selectedDeviceId = null;
      assignment.selectedDevice = null;
      assignment.selectedUnitId = null;
      assignment.selectedUnit = null;
      assignment.pendingUnitId = null;
      assignment.comment = null;
    },

    removeAssignment(index) {
      this.deviceAssignments.splice(index, 1);
    },

    addAssignment() {
      const method = this.preInstallMethod;
      // For Method B: new rows also pre-filter to top-ranked type
      const topType =
        method === "high_penetration" ? this.unitTypeRanking[0] || null : null;
      this.deviceAssignments.push(this.newAssignmentRow(topType));
    },

    // -----------------------------------------------------------------------
    // QR / close
    // -----------------------------------------------------------------------
    generatePreInstallUrl(qrToken) {
      const baseUrl = window.location.origin;
      const facilityId = this.task.organization.id;
      const taskId = this.task.id;
      return `${baseUrl}/manage-tasks/${taskId}/pre-install?qrToken=${encodeURIComponent(
        qrToken
      )}&facility=${facilityId}`;
    },

    // -----------------------------------------------------------------------
    // Complete task
    // -----------------------------------------------------------------------
    async completePreInstallTask() {
      if (!this.task) return;

      this.clearAllAlerts();
      this.completing = true;

      try {
        // Rows assigned in this session (new, need device move)
        const newlyAssignedRows = this.deviceAssignments.filter(
          (a) => a.locallyAssigned
        );
        // All rows including previously saved (from DB) — for cumulative payload
        const allAssignedRows = this.deviceAssignments.filter(
          (a) => a.locallyAssigned || a.completed
        );

        // Step 1: Move each newly-assigned device to its unit
        for (const assignment of newlyAssignedRows) {
          const { moveDevice } = useDevice(assignment.selectedDevice);
          await moveDevice(this.$apollo, assignment.selectedUnitId, null);
        }

        // Step 2: Build checklist update preserving server IDs
        const existingChecklist =
          this.task.actions && Array.isArray(this.task.actions.checklist)
            ? this.task.actions.checklist
            : [];

        const checklistActions = existingChecklist.map((item, idx) => {
          const assignment = this.deviceAssignments[idx];
          if (assignment && assignment.locallyAssigned) {
            return {
              id: item.id,
              completed: true,
              comment: assignment.comment || "",
              description: `${this.formatDeviceDisplayName(
                assignment.selectedDevice
              )} → ${
                (assignment.selectedUnit && assignment.selectedUnit.name) ||
                "Unit"
              }`,
            };
          }
          return {
            id: item.id,
            completed: item.completed || false,
            comment: item.comment || "",
            description: item.description || "",
          };
        });

        // Always mark done — task can always be re-opened to add more assignments
        const resolution = "done";

        // Cumulative payload: includes DB-saved rows (completed) + new rows (locallyAssigned)
        const assignmentsPayload = allAssignedRows.map((a) => ({
          deviceName: this.formatDeviceDisplayName(a.selectedDevice),
          unitName:
            (a.selectedUnit && a.selectedUnit.name) ||
            `Unit ${a.selectedUnitId}`,
          comment: a.comment || "",
        }));

        await this.$apollo.mutate({
          mutation: UPDATE_TASK,
          variables: {
            id: this.task.id,
            assigneeId: this.task.assignee.id,
            description: this.task.description,
            dueAt: this.task.dueAt,
            organizationId: this.task.organization.id,
            priority: this.task.priority,
            resolution,
            taskTypeId: this.task.taskType.id,
            title: this.task.title,
            actions: {
              checklist: checklistActions,
              assignments: assignmentsPayload,
            },
          },
        });

        this.$emit("task-updated", {
          type: "complete_preinstall_task",
          taskId: this.task.id,
          assignedCount: allAssignedRows.length,
          resolution,
        });

        this.handleClose();
      } catch (error) {
        console.error("Error completing pre-install task:", error);
        const graphqlErrors =
          error.graphQLErrors && error.graphQLErrors.length > 0;
        if (graphqlErrors) {
          error.graphQLErrors.forEach((e) => {
            this.addLocalAlert({ type: "error", message: e.message });
          });
        } else {
          this.addLocalAlert({
            type: "error",
            message:
              error.message ||
              "Failed to complete pre-install task. Please try again.",
          });
        }
      } finally {
        this.completing = false;
      }
    },

    async handleClose() {
      this.isOpen = false;
      this.deviceAssignments = [];
      this.showSummary = false;
    },

    titleCase,
    formatDateSlashUTC,
  },
};
</script>

<style scoped>
.border-bottom {
  border-bottom: 1px solid #e0e0e0;
}

.assignment-header {
  border-bottom: 1px solid #e0e0e0;
  padding-bottom: 12px;
}

.assignment-list {
  max-height: 50vh;
  overflow-y: auto;
}

.assignment-row {
  border-bottom: 1px solid #f0f0f0;
  transition: background-color 0.2s ease;
}

.assignment-row:hover {
  background-color: #fafafa;
}

.assignment-completed {
  background-color: #f1f8e9 !important;
}

.assignment-row:last-child {
  border-bottom: none;
}

.comment-field {
  max-width: 100%;
}

.task-details {
  padding-right: 16px;
}

.qr-section {
  background-color: #fafafa;
  padding: 12px;
  border-radius: 8px;
  border: 1px solid #e0e0e0;
  height: fit-content;
}

.assigned-cell {
  display: flex;
  align-items: center;
  padding: 8px 12px;
  min-height: 40px;
}

@media (max-width: 768px) {
  .task-details {
    padding-right: 8px;
  }

  .qr-section {
    padding: 8px;
  }
}
</style>
